/**
 * TelePortal Hooks
 *
 * React Query hooks for TelePortal operations.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import { heroesKeys } from './useHeroes';
import {
  createTelePortalService,
  type CompleteTaskInput,
  type GeneratePackageInput,
  type GetTasksParams,
  type SharePackageInput,
  type TrackType,
  type UpdateTaskInput,
  type UploadDocumentInput,
} from '../services/teleportal.service';

// Query keys
export const teleportalKeys = {
  all: ['teleportal'] as const,
  milestones: (journeyId: string) => [...teleportalKeys.all, 'milestones', journeyId] as const,
  tracks: (journeyId: string) => [...teleportalKeys.all, 'tracks', journeyId] as const,
  track: (journeyId: string, trackType: TrackType) =>
    [...teleportalKeys.all, 'track', journeyId, trackType] as const,
  tasks: (journeyId: string, params?: GetTasksParams) =>
    [...teleportalKeys.all, 'tasks', journeyId, params] as const,
  task: (journeyId: string, taskId: string) =>
    [...teleportalKeys.all, 'task', journeyId, taskId] as const,
  documents: (journeyId: string) => [...teleportalKeys.all, 'documents', journeyId] as const,
  documentDownload: (journeyId: string, documentId: string) =>
    [...teleportalKeys.all, 'document-download', journeyId, documentId] as const,
  packages: (journeyId: string) => [...teleportalKeys.all, 'packages', journeyId] as const,
  package: (journeyId: string, packageId: string) =>
    [...teleportalKeys.all, 'package', journeyId, packageId] as const,
  setup: (journeyId: string) => [...teleportalKeys.all, 'setup', journeyId] as const,
  setupPrePopulate: (journeyId: string) =>
    [...teleportalKeys.all, 'setup', 'pre-populate', journeyId] as const,
  householdMembers: (journeyId: string) =>
    [...teleportalKeys.all, 'household-members', journeyId] as const,
  visaPathways: (journeyId: string) =>
    [...teleportalKeys.all, 'setup', 'visa-pathways', journeyId] as const,
  /** Nested under the roster key, so every roster mutation invalidates it too. */
  householdProposal: (journeyId: string) =>
    [...teleportalKeys.all, 'household-members', journeyId, 'proposal'] as const,
  documentVision: (documentId: string) =>
    [...teleportalKeys.all, 'document-vision', documentId] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// MILESTONES & TRACKS HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get milestones for a journey (one per track)
 */
export function useTeleportalMilestones(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.milestones(journeyId),
    queryFn: () => teleportalService.getMilestones(journeyId),
    enabled: !!journeyId,
  });
}

/**
 * Get all tracks for a journey
 */
export function useTeleportalTracks(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.tracks(journeyId),
    queryFn: () => teleportalService.getTracks(journeyId),
    enabled: !!journeyId,
  });
}

/**
 * Get specific track details
 */
export function useTeleportalTrack(journeyId: string, trackType: TrackType) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.track(journeyId, trackType),
    queryFn: () => teleportalService.getTrack(journeyId, trackType),
    enabled: !!journeyId && !!trackType,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// TASKS HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get all tasks across tracks
 */
export function useTeleportalTasks(journeyId: string, params?: GetTasksParams) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.tasks(journeyId, params),
    queryFn: () => teleportalService.getTasks(journeyId, params),
    enabled: !!journeyId,
  });
}

/**
 * Get task details
 */
export function useTeleportalTask(journeyId: string, taskId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.task(journeyId, taskId),
    queryFn: () => teleportalService.getTask(journeyId, taskId),
    enabled: !!journeyId && !!taskId,
  });
}

/**
 * Update task
 */
export function useUpdateTeleportalTask(journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input: UpdateTaskInput }) =>
      teleportalService.updateTask(journeyId, taskId, input),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.task(journeyId, taskId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tasks(journeyId) });
    },
  });
}

/**
 * Complete task
 */
export function useCompleteTeleportalTask(journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: ({ taskId, input }: { taskId: string; input?: CompleteTaskInput }) =>
      teleportalService.completeTask(journeyId, taskId, input),
    onSuccess: (_, { taskId }) => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.task(journeyId, taskId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tasks(journeyId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tracks(journeyId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.milestones(journeyId) });
      queryClient.invalidateQueries({ queryKey: heroesKeys.miles() });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// DOCUMENTS HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get all documents for a journey
 */
export function useTeleportalDocuments(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.documents(journeyId),
    queryFn: () => teleportalService.getDocuments(journeyId),
    enabled: !!journeyId,
  });
}

/**
 * Upload document
 */
export function useUploadTeleportalDocument(journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (input: UploadDocumentInput) => teleportalService.uploadDocument(journeyId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.documents(journeyId) });
    },
  });
}

/**
 * Validate document
 */
export function useValidateTeleportalDocument(journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (docId: string) => teleportalService.validateDocument(journeyId, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.documents(journeyId) });
    },
  });
}

/**
 * Delete document
 */
export function useDeleteTeleportalDocument(journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (documentId: string) => teleportalService.deleteDocument(journeyId, documentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.documents(journeyId) });
    },
  });
}

/**
 * Get document download URL (presigned)
 */
export function useTeleportalDocumentDownloadUrl(journeyId: string, documentId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.documentDownload(journeyId, documentId),
    queryFn: () => teleportalService.getDocumentDownloadUrl(journeyId, documentId),
    enabled: !!journeyId && !!documentId,
  });
}

/**
 * Fetch download URL for a document.
 * Returns { downloadUrl, expiresAt } — consumer handles opening/downloading.
 */
export function useDownloadTeleportalDocument(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (documentId: string) =>
      teleportalService.getDocumentDownloadUrl(journeyId, documentId),
  });
}

/** BL-090 — download the selected documents as a single ZIP blob. */
export function useDownloadTeleportalDocumentsZip(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (documentIds: string[]) =>
      teleportalService.downloadDocumentsZip(journeyId, documentIds),
  });
}

// ═══════════════════════════════════════════════════════════════════════
// PACKAGES HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get all packages for a journey
 */
export function useTeleportalPackages(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.packages(journeyId),
    queryFn: () => teleportalService.getPackages(journeyId),
    enabled: !!journeyId,
  });
}

/**
 * Get package details
 */
export function useTeleportalPackage(journeyId: string, packageId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.package(journeyId, packageId),
    queryFn: () => teleportalService.getPackage(journeyId, packageId),
    enabled: !!journeyId && !!packageId,
  });
}

/**
 * Generate package
 */
export function useGenerateTeleportalPackage(journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (input: GeneratePackageInput) =>
      teleportalService.generatePackage(journeyId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.packages(journeyId) });
    },
  });
}

/**
 * Share package
 */
export function useShareTeleportalPackage(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: ({ packageId, input }: { packageId: string; input: SharePackageInput }) =>
      teleportalService.sharePackage(journeyId, packageId, input),
  });
}

// ═══════════════════════════════════════════════════════════════════════
// SETUP PROFILE HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get setup profile for a journey
 */
export function useTeleportalSetupProfile(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.setup(journeyId),
    queryFn: () => teleportalService.getSetupProfile(journeyId),
    enabled: !!journeyId,
  });
}

/**
 * Instantiate a Relocation Kit from dimensions (Brain.Learning).
 * Used by the Setup Guide processing step.
 */
export function useInstantiateTeleportalKit() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (data: {
      journeyId: string;
      dimensions: Record<string, unknown>;
      origin: string;
      destination: string;
      hasSponsor: boolean;
    }) =>
      teleportalService.instantiateKit(
        data.journeyId,
        data.dimensions,
        data.origin,
        data.destination,
        data.hasSponsor
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: teleportalKeys.householdMembers(variables.journeyId),
      });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.documents(variables.journeyId) });
    },
  });
}

/**
 * Activate a pre-generated Relocation Kit (no second Brain call).
 * Used when the user accepts the kit from the guide flow.
 */
export function useActivateTeleportalKit() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (data: {
      journeyId: string;
      dimensions: Record<string, unknown>;
      origin: string;
      destination: string;
      hasSponsor: boolean;
      kit: Record<string, unknown>;
      modifications?: { removed_task_codes: string[] };
    }) => {
      const { journeyId, ...payload } = data;
      return teleportalService.activateKit(journeyId, payload);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.setup(variables.journeyId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tracks(variables.journeyId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tasks(variables.journeyId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.milestones(variables.journeyId) });
      queryClient.invalidateQueries({
        queryKey: teleportalKeys.householdMembers(variables.journeyId),
      });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.documents(variables.journeyId) });
    },
  });
}

/**
 * Save setup profile
 */
export function useSaveTeleportalSetup() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (data: {
      journeyId: string;
      dimensions: Record<string, unknown>;
      origin: string;
      destination: string;
      hasSponsor: boolean;
    }) =>
      teleportalService.saveSetupProfile(
        data.journeyId,
        data.dimensions,
        data.origin,
        data.destination,
        data.hasSponsor
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.setup(variables.journeyId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tracks(variables.journeyId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tasks(variables.journeyId) });
      queryClient.invalidateQueries({
        queryKey: teleportalKeys.householdMembers(variables.journeyId),
      });
    },
  });
}

/**
 * Refresh setup (re-generate tracks/tasks from updated dimensions)
 */
export function useRefreshTeleportalSetup() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (data: {
      journeyId: string;
      dimensions: Record<string, unknown>;
      origin: string;
      destination: string;
      hasSponsor: boolean;
    }) =>
      teleportalService.refreshSetup(
        data.journeyId,
        data.dimensions,
        data.origin,
        data.destination,
        data.hasSponsor
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.all });
    },
  });
}

/**
 * Get pre-populated setup data for a journey
 */
export function usePrePopulateSetup(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.setupPrePopulate(journeyId),
    queryFn: () => teleportalService.getPrePopulate(journeyId),
    enabled: !!journeyId,
  });
}

// ═══════════════════════════════════════════════════════════════════════
// HOUSEHOLD MEMBERS HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get household members for a journey.
 *
 * `includeSelf` adds the applicant's own roster row (`isSelf`). Pass it when
 * the surface addresses members individually — a per-member filter bar, a
 * per-member task or document column — and leave it off when the surface is
 * about the people around the applicant. It is opt-in for the reason in
 * `TelePortalService.getHouseholdMembers`.
 *
 * The flag is part of the query key: two callers on the same screen asking for
 * different rosters must not share one cache entry.
 */
export function useTeleportalHouseholdMembers(journeyId: string, opts?: { includeSelf?: boolean }) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);
  const includeSelf = opts?.includeSelf === true;

  return useQuery({
    queryKey: [...teleportalKeys.householdMembers(journeyId), { includeSelf }],
    queryFn: () => teleportalService.getHouseholdMembers(journeyId, { includeSelf }),
    enabled: !!journeyId,
  });
}

/**
 * The visa pathways the setup step offers (F38) — see
 * `TelePortalService.getVisaPathways`. Catalog data: it changes only with a
 * brain deploy, so it is cached for the session.
 */
export function useTeleportalVisaPathways(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.visaPathways(journeyId),
    queryFn: () => teleportalService.getVisaPathways(journeyId),
    enabled: !!journeyId,
    staleTime: Infinity,
  });
}

/**
 * The household step's prefill (F35 · D-17) — see
 * `TelePortalService.getHouseholdProposal`.
 */
export function useTeleportalHouseholdProposal(journeyId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.householdProposal(journeyId),
    queryFn: () => teleportalService.getHouseholdProposal(journeyId),
    enabled: !!journeyId,
  });
}

/**
 * Create a household member
 */
export function useCreateHouseholdMember() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (data: { journeyId: string; member: Record<string, unknown> }) =>
      teleportalService.createHouseholdMember(data.journeyId, data.member),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: teleportalKeys.householdMembers(variables.journeyId),
      });
    },
  });
}

/**
 * Update a household member
 */
export function useUpdateHouseholdMember() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (data: { journeyId: string; memberId: string; updates: Record<string, unknown> }) =>
      teleportalService.updateHouseholdMember(data.journeyId, data.memberId, data.updates),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: teleportalKeys.householdMembers(variables.journeyId),
      });
    },
  });
}

/**
 * Archive a household member
 */
export function useArchiveHouseholdMember() {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (data: { journeyId: string; memberId: string }) =>
      teleportalService.archiveHouseholdMember(data.journeyId, data.memberId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: teleportalKeys.householdMembers(variables.journeyId),
      });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tasks(variables.journeyId) });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// ENHANCED TASKS HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Reopen a completed task
 */
export function useReopenTeleportalTask(journeyId: string) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const teleportalService = createTelePortalService(client);

  return useMutation({
    mutationFn: (data: { taskId: string; reason?: string }) =>
      teleportalService.reopenTask(journeyId, data.taskId, data.reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tasks(journeyId) });
      queryClient.invalidateQueries({ queryKey: teleportalKeys.tracks(journeyId) });
    },
  });
}

// ═══════════════════════════════════════════════════════════════════════
// DOCUMENT VISION HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get document vision extraction result
 */
export function useDocumentVisionResult(journeyId: string, documentId: string) {
  const { client } = useSdkContext();
  const teleportalService = createTelePortalService(client);

  return useQuery({
    queryKey: teleportalKeys.documentVision(documentId),
    queryFn: () => teleportalService.getDocumentVisionResult(journeyId, documentId),
    enabled: !!journeyId && !!documentId,
  });
}
